import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  Loader2, 
  Settings, 
  Mail, 
  Send, 
  Save,
  Eye,
  EyeOff,
  Building,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const SystemSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // Settings state
  const [settings, setSettings] = useState({
    smtp: {
      smtp2go_api_key: '',
      from_email: ''
    },
    app: {
      site_name: 'PTC Builder',
      support_email: '',
      admin_notification_email: '',
      default_trial_days: 14,
      maintenance_mode: false,
      maintenance_message: ''
    }
  });
  
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      setSettings({
        smtp: response.data.smtp || { smtp2go_api_key: '', from_email: '' },
        app: response.data.app || {
          site_name: 'PTC Builder',
          support_email: '',
          admin_notification_email: '',
          default_trial_days: 14,
          maintenance_mode: false,
          maintenance_message: ''
        }
      });
      setLastUpdated(response.data.updated_at);
      setUpdatedBy(response.data.updated_by);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin/dashboard');
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved successfully');
      fetchSettings(); // Refresh to get updated timestamps
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTest(true);
    try {
      await api.post('/admin/settings/test-email', { to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const updateSmtpSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      smtp: { ...prev.smtp, [field]: value }
    }));
  };

  const updateAppSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      app: { ...prev.app, [field]: value }
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="system-settings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-slate-900">System Settings</h1>
            <p className="text-slate-500">
              Configure system-wide settings for PTC Builder
            </p>
            {lastUpdated && (
              <p className="text-xs text-slate-400 mt-1">
                Last updated: {new Date(lastUpdated).toLocaleString()} by {updatedBy}
              </p>
            )}
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>

        {/* SMTP2Go Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" />
              Email Configuration (SMTP2Go)
            </CardTitle>
            <CardDescription>
              Configure SMTP2Go for sending password reset emails and notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp2go_api_key">SMTP2Go API Key</Label>
                <div className="relative">
                  <Input
                    id="smtp2go_api_key"
                    type={showApiKey ? "text" : "password"}
                    placeholder="Enter your SMTP2Go API key"
                    value={settings.smtp.smtp2go_api_key || ''}
                    onChange={(e) => updateSmtpSetting('smtp2go_api_key', e.target.value)}
                    className="pr-10"
                    data-testid="smtp-api-key-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4 text-slate-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-400" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Get your API key from <a href="https://www.smtp2go.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">smtp2go.com</a>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="from_email">From Email Address</Label>
                <Input
                  id="from_email"
                  type="email"
                  placeholder="noreply@yourdomain.com"
                  value={settings.smtp.from_email || ''}
                  onChange={(e) => updateSmtpSetting('from_email', e.target.value)}
                  data-testid="smtp-from-email-input"
                />
                <p className="text-xs text-slate-500">
                  The email address that will appear in the From field
                </p>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="test_email">Send Test Email</Label>
                <Input
                  id="test_email"
                  type="email"
                  placeholder="Enter email to send test"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  data-testid="test-email-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleTestEmail}
                disabled={sendingTest || !settings.smtp.smtp2go_api_key}
                data-testid="send-test-email-btn"
              >
                {sendingTest ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Test
              </Button>
            </div>
            
            {!settings.smtp.smtp2go_api_key?.includes('*') && !settings.smtp.smtp2go_api_key && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-amber-700">
                  SMTP2Go is not configured. Email features (password reset, notifications) will not work.
                </p>
              </div>
            )}
            
            {settings.smtp.smtp2go_api_key && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <p className="text-sm text-emerald-700">
                  SMTP2Go is configured. Use the test button above to verify it works.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* App Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <Building className="h-5 w-5 text-indigo-600" />
              Application Settings
            </CardTitle>
            <CardDescription>
              General application configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="site_name">Site Name</Label>
                <Input
                  id="site_name"
                  placeholder="PTC Builder"
                  value={settings.app.site_name || ''}
                  onChange={(e) => updateAppSetting('site_name', e.target.value)}
                  data-testid="site-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="default_trial_days">Default Trial Period (Days)</Label>
                <Input
                  id="default_trial_days"
                  type="number"
                  min="1"
                  max="365"
                  value={settings.app.default_trial_days || 14}
                  onChange={(e) => updateAppSetting('default_trial_days', parseInt(e.target.value) || 14)}
                  data-testid="trial-days-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="support_email">Support Email</Label>
                <Input
                  id="support_email"
                  type="email"
                  placeholder="support@yourdomain.com"
                  value={settings.app.support_email || ''}
                  onChange={(e) => updateAppSetting('support_email', e.target.value)}
                  data-testid="support-email-input"
                />
                <p className="text-xs text-slate-500">
                  Displayed to users who need help
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin_notification_email">Admin Notification Email</Label>
                <Input
                  id="admin_notification_email"
                  type="email"
                  placeholder="admin@yourdomain.com"
                  value={settings.app.admin_notification_email || ''}
                  onChange={(e) => updateAppSetting('admin_notification_email', e.target.value)}
                  data-testid="admin-notification-email-input"
                />
                <p className="text-xs text-slate-500">
                  Receives payment notifications and alerts
                </p>
              </div>
            </div>

            <Separator className="my-4" />

            {/* Maintenance Mode */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="maintenance_mode">Maintenance Mode</Label>
                  <p className="text-xs text-slate-500">
                    When enabled, only system admins can access the application
                  </p>
                </div>
                <Switch
                  id="maintenance_mode"
                  checked={settings.app.maintenance_mode || false}
                  onCheckedChange={(checked) => updateAppSetting('maintenance_mode', checked)}
                  data-testid="maintenance-mode-switch"
                />
              </div>
              
              {settings.app.maintenance_mode && (
                <div className="space-y-2">
                  <Label htmlFor="maintenance_message">Maintenance Message</Label>
                  <Input
                    id="maintenance_message"
                    placeholder="We're currently performing maintenance. Please check back soon."
                    value={settings.app.maintenance_message || ''}
                    onChange={(e) => updateAppSetting('maintenance_message', e.target.value)}
                    data-testid="maintenance-message-input"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button (bottom) */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-settings-btn-bottom"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SystemSettingsPage;
